import * as React from 'react';

// Utils
import { navigate } from '@toolkit/util/app';
import { t } from '@toolkit/util/i18n';
// Constants
import { ENVIRONMENT, STATUS } from '@src/shared/src/const/app';
import { ROUTES } from '@toolkit/const/app';
// Actions, Models & Interfaces
import { HotelModel } from '@src/shared/src/models';
import { selectors } from '@src/shared/src';
import { appSettingsSelectors } from '@src/shared/src/selectors';
import { IRootState } from '@src/store';
// Components
import {
  CheckoutSideBarHotelInfo,
  CheckoutSideBarTripInfo,
  CheckoutSidebarConfirmBody,
  CheckoutTimer,
} from '@pod/checkout/components';
import { SearchSideBarInfo } from '@pod/search/components';
import { useSelector } from 'react-redux';
import { isMultiCitySearch } from '@src/shared/src/util/search';
import CheckoutSideBarRentalInfo from '../components/CheckoutSideBarRentalInfo';
import CheckoutSideBarCo2Info from '../components/CheckoutSideBarCo2Info';
// Styles

type Props = {
  bookingId: string;
  env: ENVIRONMENT;
  isBasketEditable: boolean;
  onTimerExpired: () => void;
  exceedsAllowance?: boolean;
};

const CheckoutSideBarConn: React.FC<Props> = ({
  bookingId,
  env,
  isBasketEditable,
  onTimerExpired,
  exceedsAllowance,
}) => {
  const search = useSelector((state: IRootState) => selectors.search.search(state.search));
  const trips = useSelector((state: IRootState) => selectors.checkout.sortedTrips(state.checkout));
  const hotels = useSelector((state: IRootState) => state.checkout.hotels);
  const rentals = useSelector((state: IRootState) => state.checkout.rentals);
  const basketStatus = useSelector(selectors.checkout.basketStatus);
  const preparedAt = useSelector((state: IRootState) =>
    selectors.checkout.preparedAt(state.checkout),
  );
  const loyaltyCards = useSelector(appSettingsSelectors.membershipCards);

  return (
    <aside className="sidebar sidebar-trip is-open">
      <div className="sidebar-wrapper">
        {env !== ENVIRONMENT.CONFIRMATION && basketStatus === STATUS.PREPARE_FINISHED && (
          <CheckoutTimer onTimerExpired={onTimerExpired} bookingCreatedAt={preparedAt} />
        )}

        {env === ENVIRONMENT.CONFIRMATION ? (
          <CheckoutSidebarConfirmBody basketStatus={basketStatus as STATUS} bookingId={bookingId} />
        ) : (
          <>
            <SearchSideBarInfo
              hideResetButton={exceedsAllowance}
              search={search}
              loyaltyCards={loyaltyCards}
            />

            {(trips ?? []).map((trip, idx) => (
              <CheckoutSideBarTripInfo
                key={`trip-${idx}`}
                title={
                  isMultiCitySearch(search)
                    ? `${t('TripSideBarInfo.route')} ${idx + 1}`
                    : idx === 0
                    ? t('CheckoutSideBarTripInfo.title.outboundTrip')
                    : t('CheckoutSideBarTripInfo.title.inboundTrip')
                }
                trip={trip}
                onEdit={
                  isBasketEditable
                    ? () =>
                        navigate(
                          `${idx === 0 ? ROUTES.TRIPS.OUTWARD : ROUTES.TRIPS.INBOUND}${search.id}`,
                        )
                    : null
                }
              />
            ))}

            {(hotels ?? []).map((hotel: HotelModel, idx: number) => (
              <CheckoutSideBarHotelInfo
                key={`hotel-${idx}`}
                title={t('CheckoutSideBarTripInfo.title.hotel')}
                hotelName={hotel.name}
                checkinDate={search.depAt}
                checkoutDate={search.arrAt}
                travellersCount={search.passengers.length}
                roomCount={search.hotelRoomCount}
                onEdit={isBasketEditable ? () => navigate(`${ROUTES.HOTELS}${search.id}`) : null}
              />
            ))}

            {(rentals ?? []).map((rental, idx) => (
              <CheckoutSideBarRentalInfo key={`rental-${idx}`} rental={rental} />
            ))}

            <CheckoutSideBarCo2Info
              showTransportCo2Total={!!trips?.length}
              showHotelCo2Total={!!hotels?.length}
              showRentalCo2Total={!!rentals?.length}
            />
          </>
        )}
      </div>
    </aside>
  );
};

export default CheckoutSideBarConn;
